// src/components/common/PageBreadcrumb.tsx
import Link from "next/link";
import React from "react";

interface BreadcrumbItem {
  label: string;
  href: string;
  active?: boolean;
  icon?: React.ReactNode;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  showTitle?: boolean;
  className?: string;
}

const PageBreadcrumb: React.FC<BreadcrumbProps> = ({ 
  items, 
  showTitle = true,
  className = ''
}) => {
  // Get the last item as the page title
  const pageTitle = items[items.length - 1]?.label || '';
  
  // Filter out empty items
  const filteredItems = items.filter(item => item.label && item.href);

  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 mb-6 ${className}`}>
      {showTitle && (
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
          {pageTitle}
        </h2>
      )}
      
      <nav aria-label="Breadcrumb">
        <ol className="flex items-center gap-2">
          {filteredItems.map((item, index) => (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && (
                <svg
                  className="stroke-current text-gray-400 dark:text-gray-500"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 11L9 7L5 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              
              {index < filteredItems.length - 1 ? (
                <Link
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                  href={item.href}
                >
                  {item.icon && (
                    <span className="w-4 h-4">{item.icon}</span>
                  )}
                  {item.label}
                </Link>
              ) : (
                <span className={`text-sm flex items-center gap-1.5 ${
                  item.active 
                    ? 'text-brand-600 dark:text-brand-400 font-medium' 
                    : 'text-gray-800 dark:text-white/90'
                }`}>
                  {item.icon && (
                    <span className="w-4 h-4">{item.icon}</span>
                  )}
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};

export default PageBreadcrumb;